import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle, Hourglass, XCircle } from "@phosphor-icons/react";
import { api } from "@/lib/api";

export default function PaymentResult() {
  const [params] = useSearchParams();
  const orderId = params.get("order_id");
  const [order, setOrder] = useState(null);
  const [tries, setTries] = useState(0);

  useEffect(() => {
    if (!orderId) return;
    api.post(`/orders/${orderId}/verify`).then(({ data }) => setOrder(data)).catch(() => setOrder({ status: "error" }));
  }, [orderId, tries]);

  useEffect(() => {
    if (order?.status === "pending" && tries < 6) {
      const t = setTimeout(() => setTries((n) => n + 1), 4000);
      return () => clearTimeout(t);
    }
  }, [order, tries]);

  const paid = ["paid", "packed", "dispatched", "delivered"].includes(order?.status);

  return (
    <div className="mx-auto max-w-2xl px-5 py-24 text-center" data-testid="payment-result-page">
      {!order ? (
        <p className="text-sm text-muted-foreground" data-testid="payment-verifying">Confirming your payment…</p>
      ) : paid ? (
        <>
          <CheckCircle weight="duotone" size={64} className="mx-auto text-accent" />
          <p className="eyebrow mt-6">Order {order.order_id}</p>
          <h1 className="mt-3 text-4xl font-medium tracking-tight" data-testid="payment-success-title">Thank you. It's yours.</h1>
          <p className="mt-4 text-base text-foreground/75">
            {order.has_ebook && "Your personalised PDF is ready to download. "}
            {order.has_physical && "Your paperback will be packed and dispatched within 2–3 working days. "}
            A confirmation with your GST invoice is on its way to {order.customer_email}.
          </p>
          <Link to={`/account/orders/${order.order_id}`} className="mt-8 inline-block rounded-full bg-primary px-7 py-3.5 text-sm font-semibold text-white" data-testid="view-order-button">
            {order.has_ebook ? "Download your ebook" : "View order"}
          </Link>
        </>
      ) : order.status === "pending" ? (
        <>
          <Hourglass weight="duotone" size={64} className="mx-auto text-primary" />
          <h1 className="mt-6 text-3xl font-medium tracking-tight" data-testid="payment-pending-title">Waiting for the bank to confirm…</h1>
          <p className="mt-4 text-sm text-muted-foreground">This usually takes a few seconds. If you closed the payment window, nothing was charged — you can try again from your orders.</p>
          <div className="mt-8 flex justify-center gap-3">
            <button onClick={() => setTries((n) => n + 1)} className="rounded-full border border-foreground/20 px-5 py-2.5 text-sm font-semibold" data-testid="payment-retry-button">Check again</button>
            <Link to="/account" className="rounded-full bg-foreground px-5 py-2.5 text-sm font-semibold text-white" data-testid="payment-orders-link">My orders</Link>
          </div>
        </>
      ) : (
        <>
          <XCircle weight="duotone" size={64} className="mx-auto text-destructive" />
          <h1 className="mt-6 text-3xl font-medium tracking-tight" data-testid="payment-failed-title">Payment didn't go through.</h1>
          <p className="mt-4 text-sm text-muted-foreground">No money was taken. Your bag is empty now — add the titles again and retry.</p>
          <Link to="/#shelf" className="mt-8 inline-block rounded-full bg-foreground px-7 py-3.5 text-sm font-semibold text-white" data-testid="payment-back-link">Back to the shelf</Link>
        </>
      )}
    </div>
  );
}
